/**
 * An editable dropdown must always have something to drop down.
 *
 * WigMetadataForm decides a select's option source from the metadata row:
 *   - `distinct_values_lookup` set  -> async LookupSelect, options from
 *     `lookupFetchers[thatKey]`
 *   - otherwise                     -> static Select, options from the inline
 *     `options` array the server builds from `pick_lists`
 *
 * Either source can come up empty for reasons the user can do nothing about:
 * the surface never wired a fetcher for that lookup key, or the field's
 * `pick_lists` rows were never seeded (the vocabulary migrations run
 * out-of-band, so a metadata row can say "managed pick list" in a database
 * where the values do not exist yet — `deals.deal_status`, migration 083, is
 * exactly this shape). Either way the user gets a control they are allowed to
 * edit and a dropdown reading "No data".
 *
 * This module closes that gap client-side: any visible, editable select with
 * no reachable option source is re-pointed at a synthetic lookup key backed by
 * a caller-supplied resolver (in this app: `pick_lists` for the field, then any
 * dedicated endpoint that still serves the same vocabulary). It is a safety
 * net, not a substitute for seeding the data — but it means a half-applied
 * vocabulary migration degrades to a slower dropdown rather than an empty one.
 */

/** Namespace for the synthetic lookup keys, so they can't collide with real ones. */
export const FALLBACK_LOOKUP_PREFIX = "fallback:";

/**
 * Will this row render as a select with no options behind it?
 *
 * Mirrors the library's branch order: LongText / textarea never becomes a
 * select, a `distinct_values_lookup` wins over inline options, and everything
 * else with `input_type='select'` or `data_type='Lookup'` is a static select.
 *
 * @param {object} row a UI metadata row
 * @param {Record<string, Function>} [fetchers] the surface's lookupFetchers
 * @returns {boolean}
 */
export const needsOptionFallback = (row, fetchers) => {
  if (!row || row.hide || row.editable === false) return false;

  const inputType = String(row.input_type || "").toLowerCase();
  if (inputType === "textarea" || row.data_type === "LongText") return false;
  if (!(inputType === "select" || row.data_type === "Lookup")) return false;

  const lookup = row.distinct_values_lookup;
  // A lookup-backed field is fine as long as this surface wired the fetcher.
  if (lookup) return typeof fetchers?.[lookup] !== "function";
  // A static select is fine as long as the server inlined its options.
  return !(Array.isArray(row.options) && row.options.length > 0);
};

/**
 * Give every optionless editable select a source.
 *
 * Returns the ORIGINAL metadata and fetchers objects when nothing needed
 * patching, so callers can hold the result in a memo without churning
 * identities (which would make every LookupSelect re-fetch).
 *
 * @param {Array<object>} metadata UI metadata rows
 * @param {Record<string, Function>} fetchers the surface's lookupFetchers
 * @param {(row: object, query: string) => Promise<Array<{id: any, label: string}>>} resolve
 *   async option source for one row
 * @returns {{ metadata: Array<object>, lookupFetchers: Record<string, Function>,
 *   patchedFields: string[] }}
 */
export const withSelectFallbacks = (metadata, fetchers, resolve) => {
  const base = fetchers || {};
  if (!Array.isArray(metadata) || typeof resolve !== "function") {
    return { metadata, lookupFetchers: base, patchedFields: [] };
  }

  const patchedFields = [];
  const added = {};
  const out = metadata.map((row) => {
    if (!needsOptionFallback(row, base)) return row;
    const key = `${FALLBACK_LOOKUP_PREFIX}${row.field}`;
    patchedFields.push(row.field);
    added[key] = (query) => resolve(row, query);
    return { ...row, distinct_values_lookup: key };
  });

  if (patchedFields.length === 0) {
    return { metadata, lookupFetchers: base, patchedFields };
  }
  return {
    metadata: out,
    lookupFetchers: { ...base, ...added },
    patchedFields,
  };
};

export default withSelectFallbacks;
